import { logger } from './logger';

import type { WindowTrackerState } from '../types';

type WindowWithOptionalTrackerState = Window & {
  __tuic_tracker?: WindowTrackerState;
};

const getWindow = (): WindowWithOptionalTrackerState | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window;
};

export const getWindowTrackerState = (): WindowTrackerState => {
  const win = getWindow();

  if (typeof win === 'undefined') {
    return getInitialState();
  }

  if (typeof win.__tuic_tracker !== 'undefined') {
    return win.__tuic_tracker;
  }

  return (win.__tuic_tracker = getInitialState());
};

export const setWindowTrackerState = (
  newState:
    | Partial<WindowTrackerState>
    | ((currentState: WindowTrackerState) => WindowTrackerState),
): void => {
  const win = getWindow();

  if (typeof win === 'undefined') {
    return;
  }

  const currentState = win.__tuic_tracker ?? getInitialState();

  const finalNewState =
    typeof newState === 'function'
      ? newState(currentState)
      : {
          ...currentState,
          ...newState,
        };

  if (!statesAreEqual(currentState, finalNewState)) {
    logger.log('Updated tracker state. New state is:', finalNewState);
  }

  win.__tuic_tracker = finalNewState;
};

const getInitialState = (): WindowTrackerState => {
  return {
    ready: false,
    eventQueue: [],
    pageViewQueue: [],
    pageViewContext: undefined,
    meta: {
      isWebview: undefined,
      userIsSignedIn: undefined,
      pathname: undefined,
    },
  };
};

const statesAreEqual = (a: WindowTrackerState, b: WindowTrackerState) => {
  if (a === b) {
    return true;
  }

  if (a.ready !== b.ready) {
    return false;
  }

  if (a.pageViewQueue.length !== b.pageViewQueue.length) {
    return false;
  }

  if (a.eventQueue.length !== b.eventQueue.length) {
    return false;
  }

  if (!objectsAreEqual(a.meta, b.meta)) {
    return false;
  }

  if (!objectsAreEqual(a.pageViewContext ?? {}, b.pageViewContext ?? {})) {
    return false;
  }

  return true;
};

const objectsAreEqual = (
  a: Record<string, any>,
  b: Record<string, any>,
): boolean => {
  return (
    Object.entries(a).sort().toString() === Object.entries(b).sort().toString()
  );
};
